/**
 * signatureRequestViewModel — PURE presentation math for signature-request
 * status chips and doc-hash fingerprints, consumed inline by ContractsView.
 *
 * Keeps every color/label/tooltip decision out of the component so the UI
 * layer stays a dumb renderer and the copy is testable. Colors come from
 * the design tokens (`lib/tokens`), backgrounds are the standard soft-glass
 * tints used by the other status pills.
 */

import { C } from '../tokens';
import { shortHash } from './docHash';
import type { SignatureRequestStatus } from './signatureRequest';

export interface StatusChipModel {
  /** Human label, e.g. "Signed". */
  label: string;
  /** Foreground color (token-derived). */
  color: string;
  /** Soft tint background for the pill. */
  background: string;
  /** Tooltip explaining what the state means. */
  title: string;
}

const CHIPS: Record<SignatureRequestStatus, StatusChipModel> = {
  sent: {
    label: 'Sent',
    color: C.blueLight,
    background: 'rgba(0,130,243,0.14)',
    title: 'Signature request issued — awaiting the signer’s first view.',
  },
  viewed: {
    label: 'Viewed',
    color: C.orange,
    background: 'rgba(253,173,1,0.14)',
    title: 'The signer has opened the document but not yet signed.',
  },
  signed: {
    label: 'Signed',
    color: C.green,
    background: 'rgba(34,197,94,0.14)',
    title: 'Signed with recorded consent, bound to the exact document hash.',
  },
  declined: {
    label: 'Declined',
    color: C.orangeWarm,
    background: 'rgba(226,57,57,0.14)',
    title: 'The signer declined — the recorded reason is on the request.',
  },
  voided: {
    label: 'Voided',
    color: C.textDim,
    background: 'rgba(180,188,210,0.10)',
    title: 'Request voided before completion — terminal, cannot be signed.',
  },
};

/** Chip model for a signature-request status. Total over the union. */
export function buildStatusChip(status: SignatureRequestStatus): StatusChipModel {
  return CHIPS[status];
}

/**
 * Human fingerprint for a doc hash, e.g. "SHA-256 · ba7816bf". An empty /
 * missing hash is reported honestly instead of rendering a blank pill.
 */
export function buildHashFingerprintLabel(hex: string): string {
  const trimmed = hex.trim();
  if (trimmed === '') return 'SHA-256 · (no hash)';
  return `SHA-256 · ${shortHash(trimmed)}`;
}
